import { KnownId, StatusData } from "@fairfleet/geotab";
import { useQueries, UseQueryResult } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { fetchStatusDataComplete } from "@/utilities/query/fetchStatusDataComplete";

export const ELECTRIC_ENERGY_QUERY_KEY = "electricEnergy";

/** Cumulative counters of one device: energy taken out of the battery and energy put back in. */
export interface ElectricEnergyReadings {
  out: StatusData[];
  in: StatusData[];
}

type DeviceReadings = { deviceId: string; readings: ElectricEnergyReadings };

// Module-level so TanStack sees a stable reference and does not re-run it on every render.
function combineByDevice(results: UseQueryResult<DeviceReadings>[]) {
  const byDevice: Record<string, ElectricEnergyReadings> = {};
  for (const result of results) {
    if (result.data) byDevice[result.data.deviceId] = result.data.readings;
  }
  return {
    byDevice,
    isFetching: results.some(r => r.isFetching),
    isError: results.some(r => r.isError),
  };
}

/**
 * Energy counters per device for a period. Consumption is the difference of "energy out" minus
 * the difference of "energy in" (recuperation) over a window, which is what Geotab's fuel and
 * EV report shows as Stromverbrauch.
 */
export function useElectricEnergyForDevices(
  deviceIds: string[],
  fromDate: Date,
  toDate: Date,
  enabled = true,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQueries({
    queries: deviceIds.map(deviceId => ({
      queryKey: [ELECTRIC_ENERGY_QUERY_KEY, activeSessionId, deviceId, fromDate, toDate],
      enabled,
      queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<DeviceReadings> => {
        // Paged until the window is covered: one Get stops at the cap and would drop the newest
        // readings, understating consumption.
        const readings: ElectricEnergyReadings = { out: [], in: [] };
        await fetchStatusDataComplete(geotabClient, {
          tasks: [
            { key: "out", deviceId, diagnosticId: KnownId.DiagnosticElectricEnergyOutId },
            { key: "in", deviceId, diagnosticId: KnownId.DiagnosticElectricEnergyInId },
          ],
          fromDate,
          toDate,
          signal,
          onPage: (key, page) => readings[key as "out" | "in"].push(...page),
        });
        return { deviceId, readings };
      },
    })),
    combine: combineByDevice,
  });
}
